import { labelForStrategy } from '../../utils/strategyLabels';

/**
 * Translate a widget-config strategy value into a human-readable name.
 *
 * Real strategy sheets are referenced by their actual sheet name (e.g.
 * "Grass Fed Prime Beef (Current version 2.1C)") so they pass through
 * unchanged. User-defined Custom Multi-Strat combos are stored as
 * `custom:<id>` so we look them up in the `customCombos` list and return
 * the combo's friendly name. A deleted combo (id no longer in the list)
 * falls back to a placeholder so the widget still renders something
 * readable instead of the raw id.
 *
 * @param {string} strategy            widget.config.strategy value
 * @param {Array<{id: string, name: string}>} customCombos  user's combos
 * @returns {string}
 */
export function resolveStrategyDisplayName(strategy, customCombos) {
  if (typeof strategy !== 'string' || !strategy) return strategy || '';
  if (!strategy.startsWith('custom:')) {
    // An admin rename from Strategy Settings, if there is one. Applied here so
    // every existing caller — the risk-tool dropdowns, the dashboard widgets —
    // picks it up without threading `sheetLabels` through six component trees.
    // See utils/strategyLabels.js.
    return labelForStrategy(strategy) || strategy;
  }
  const id = strategy.slice('custom:'.length);
  const combo = (customCombos || []).find((c) => c && c.id === id);
  return combo ? combo.name : 'Custom strat combo (deleted)';
}

/** Sheet names with `custom:<id>` entries removed — use when combos are listed separately. */
export function regularSheetNamesOnly(sheetNames) {
  return (sheetNames || []).filter(
    (s) => s !== 'Sample Strategy (Demo)' && !(typeof s === 'string' && s.startsWith('custom:')),
  );
}
